import { UseFormSetValue } from 'react-hook-form';
import { Dispatch, SetStateAction, useState } from 'react';
import { generateTemplate } from '../../../../api/users-api';
import {
  BulkImportType,
  TemplateGenerationRequest,
} from '../../../../../types';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { calendarEventTemplateHeaders } from './spreadsheet-template/calendar-event-template-headers';
import { calendarEventTemplateColumnValidation } from './spreadsheet-template/calendar-event-template-column-validation';

/**
 * Creates a pre-formatted Google Sheet for the bulk import type, selects it as
 * the spreadsheet for this bulk operation and opens it in a new tab.
 */
export function useGenerateTemplate(
  setValue: UseFormSetValue<BulkOpsFormValues>,
  setSpreadSheetName: Dispatch<SetStateAction<string>>,
  selectedBulkImportType: BulkImportType | null
) {
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);

  const handleUseTemplate = async () => {
    if (!selectedBulkImportType) {
      return;
    }

    setIsGeneratingTemplate(true);
    try {
      const isCalendarImport =
        selectedBulkImportType.name === 'calendarEventsImport';

      const templateRequest: TemplateGenerationRequest = {
        templateName: selectedBulkImportType.niceName,
      };

      if (isCalendarImport) {
        templateRequest.sheetColumnHeaders = calendarEventTemplateHeaders;
        templateRequest.columnValidation =
          calendarEventTemplateColumnValidation;
      } else {
        templateRequest.columnHeaders =
          selectedBulkImportType.name === 'bulkAssignAuLicenses'
            ? ['User Email', 'Status']
            : selectedBulkImportType.columns.map((col) => col.columnName);
        if (selectedBulkImportType.name === 'bulkAssignAuLicenses') {
          templateRequest.columnNotes = {
            'User Email': 'This value is required to run this bulk operation',
          };
        }
      }

      const templateResponse = await generateTemplate(templateRequest);

      // Extract spreadsheet ID and the actual file name from the response
      const spreadsheetId = templateResponse.spreadsheetId;
      const spreadsheetName = templateResponse.templateName + ' Template';

      if (!spreadsheetId) {
        throw new Error('No spreadsheet ID returned from template generation');
      }

      const now = new Date();
      const dateString = now.toLocaleString(undefined, {
        hour12: true,
        timeZoneName: 'short',
      });
      const chipLabel = `${spreadsheetName} - ${dateString}`;
      setValue('spreadsheetId', spreadsheetId);
      setSpreadSheetName(chipLabel);

      // Open the template in a new tab
      window.open(
        `https://docs.google.com/spreadsheets/d/${spreadsheetId}`,
        '_blank'
      );
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  return { handleUseTemplate, isGeneratingTemplate };
}
